/**
 * Parse Cadwork Material column values into proposal species + grade.
 *
 * Examples:
 *   "Doug Fir - SS" → Douglas Fir / Select
 *   "Doug Fir"      → Douglas Fir / #1
 *   "White Oak"     → White Oak / #2
 *   "EWP"           → Eastern White Pine / #2
 *   "BDF"           → Douglas Fir / #1
 */

import { TIMBER_SPECIES_INFO } from '../types/timberPricing';

export const DEFAULT_FALLBACK_SPECIES = 'Eastern White Pine';
export const DEFAULT_FALLBACK_GRADE = '#2';

/** Cadwork grade suffix codes → materials DB grade labels. */
export const GRADE_SUFFIX_ALIASES: Record<string, string> = {
  SS: 'Select',
  'SELECT STRUCTURAL': 'Select',
  'SELECT STRUCT': 'Select',
  'SEL STRUCT': 'Select',
  'SEL STRUCTURAL': 'Select',
};

/** Default grade when Material has no explicit suffix. Key = canonical species name. */
export const SPECIES_DEFAULT_GRADE: Record<string, string> = {
  'Douglas Fir': '#1',
  'White Oak': '#2',
  'Eastern White Pine': '#2',
};

/** Legacy Cadwork material codes without grade suffix. */
const LEGACY_MATERIAL_ALIASES: Record<string, string> = {
  bdf: 'Douglas Fir',
  df: 'Douglas Fir',
  ewp: 'Eastern White Pine',
  wo: 'White Oak',
  oak: 'White Oak',
};

export interface ParsedTimberMaterial {
  species: string;
  grade: string;
  /** True when grade came from an explicit " - CODE" suffix in Material. */
  gradeFromSuffix: boolean;
  /** KD / GRN written on the material cell. Empty when the cell doesn't say. */
  moistureContent: '' | 'green' | 'kiln_dried';
}

function normalizeKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

function buildSpeciesAliasMap(): Map<string, string> {
  const map = new Map<string, string>();
  for (const info of Object.values(TIMBER_SPECIES_INFO)) {
    map.set(normalizeKey(info.name), info.name);
    map.set(normalizeKey(info.abbrev), info.name);
    for (const alias of info.aliases) {
      map.set(normalizeKey(alias), info.name);
    }
  }
  map.set(normalizeKey('ewp'), 'Eastern White Pine');
  map.set(normalizeKey('eastern white pine'), 'Eastern White Pine');
  map.set(normalizeKey('white pine'), 'Eastern White Pine');
  map.set(normalizeKey('white oak'), 'White Oak');
  map.set(normalizeKey('wo'), 'White Oak');
  map.set(normalizeKey('bdf'), 'Douglas Fir');
  for (const [alias, canonical] of Object.entries(LEGACY_MATERIAL_ALIASES)) {
    map.set(alias, canonical);
  }
  return map;
}

const SPECIES_ALIAS_MAP = buildSpeciesAliasMap();

function resolveCanonicalSpecies(speciesPart: string): string | null {
  const key = normalizeKey(speciesPart);
  if (!key) return null;
  const direct = SPECIES_ALIAS_MAP.get(key);
  if (direct) return direct;
  const compact = key.replace(/\s+/g, '');
  let matched: string | null = null;
  SPECIES_ALIAS_MAP.forEach((canonical, alias) => {
    if (matched) return;
    if (alias.replace(/\s+/g, '') === compact) matched = canonical;
  });
  return matched;
}

export function resolveGradeSuffix(suffix: string): string | null {
  const trimmed = suffix.trim();
  if (!trimmed) return null;
  const fromAlias = GRADE_SUFFIX_ALIASES[trimmed.toUpperCase()];
  if (fromAlias) return fromAlias;
  const normalized = trimmed.toLowerCase().replace(/\s+/g, ' ');
  if (normalized === 'select structural' || normalized === 'select struct' || normalized === 'sel struct') {
    return 'Select';
  }
  if (/^#\d+$/.test(trimmed)) return trimmed;
  if (normalized === 'select') return 'Select';
  return null;
}

/** True when grade is the species default (e.g. Douglas Fir / #1). */
export function isDefaultSpeciesGrade(grade: string, species: string): boolean {
  const g = (grade || '').trim();
  if (!g) return true;
  return g === (SPECIES_DEFAULT_GRADE[species] ?? DEFAULT_FALLBACK_GRADE);
}

/** True when grade is explicitly set (not species default). */
export function isExplicitTimberGrade(grade: string, species: string): boolean {
  return !isDefaultSpeciesGrade(grade, species);
}

/** True when a CSV cell looks like a standalone grade code (SS, #1, Select, …). */
export function isTimberGradeSuffixCell(value: string): boolean {
  return resolveGradeSuffix(value) !== null;
}

const MATERIAL_GRADE_DASHES = [' - ', ' – ', ' — '] as const;

function splitMaterialAndGradeSuffix(trimmed: string): { speciesPart: string; gradeSuffix: string | null } {
  for (const sep of MATERIAL_GRADE_DASHES) {
    const dashIdx = trimmed.lastIndexOf(sep);
    if (dashIdx > 0) {
      return {
        speciesPart: trimmed.slice(0, dashIdx).trim(),
        gradeSuffix: trimmed.slice(dashIdx + sep.length).trim(),
      };
    }
  }

  const tightDash = trimmed.match(/^(.+?)\s*-\s*(\S.+)$/);
  if (tightDash) {
    return { speciesPart: tightDash[1]!.trim(), gradeSuffix: tightDash[2]!.trim() };
  }

  const trailingGrade = trimmed.match(/^(.+?)\s+(SS|Select Structural|Select|#\d+)$/i);
  if (trailingGrade) {
    return { speciesPart: trailingGrade[1]!.trim(), gradeSuffix: trailingGrade[2]!.trim() };
  }

  return { speciesPart: trimmed, gradeSuffix: null };
}

function defaultGradeForSpecies(species: string, gradeFallback: string): string {
  return SPECIES_DEFAULT_GRADE[species] ?? gradeFallback;
}

function moistureFromToken(token: string): 'green' | 'kiln_dried' | null {
  const key = token.trim().toLowerCase().replace(/[\s-]+/g, ' ');
  if (key === 'kd' || key === 'kiln dried') return 'kiln_dried';
  if (key === 'green' || key === 'grn') return 'green';
  return null;
}

/** Pull a leading/trailing KD or GRN token off a material fragment. */
function stripMoistureTokens(value: string): { text: string; moisture: '' | 'green' | 'kiln_dried' } {
  let moisture: '' | 'green' | 'kiln_dried' = '';
  const text = value
    .replace(/\b(?:kiln[\s-]*dried|kd|green|grn)\b/gi, (match) => {
      const parsed = moistureFromToken(match);
      if (parsed && !moisture) moisture = parsed;
      return ' ';
    })
    .replace(/\s+/g, ' ')
    .trim();
  return { text, moisture };
}

/** Strip trailing " - GRADE" suffix for species-only matching (RFQ filters). */
export function stripMaterialGradeSuffix(material: string): string {
  const trimmed = material.trim();
  const dashIdx = trimmed.lastIndexOf(' - ');
  if (dashIdx <= 0) return trimmed;
  return trimmed.slice(0, dashIdx).trim();
}

export interface ParseTimberMaterialOptions {
  speciesFallback?: string;
  gradeFallback?: string;
}

/**
 * Parse a Cadwork Material cell into species and grade for proposal timber lines.
 */
export function parseTimberMaterialColumn(
  material: string,
  options?: ParseTimberMaterialOptions,
): ParsedTimberMaterial {
  const speciesFallback = options?.speciesFallback ?? DEFAULT_FALLBACK_SPECIES;
  const gradeFallback = options?.gradeFallback ?? DEFAULT_FALLBACK_GRADE;
  const trimmed = material.trim();

  if (!trimmed) {
    return { species: speciesFallback, grade: gradeFallback, gradeFromSuffix: false, moistureContent: '' };
  }

  const { speciesPart: rawSpeciesPart, gradeSuffix } = splitMaterialAndGradeSuffix(trimmed);
  const speciesMoisture = stripMoistureTokens(rawSpeciesPart);
  const suffixMoisture = gradeSuffix ? moistureFromToken(gradeSuffix) : null;
  const speciesPart = speciesMoisture.text;
  const moistureContent = speciesMoisture.moisture || suffixMoisture || '';

  const canonicalSpecies = resolveCanonicalSpecies(speciesPart);
  const species = canonicalSpecies ?? (speciesPart || speciesFallback);

  if (gradeSuffix && !suffixMoisture) {
    const explicitGrade = resolveGradeSuffix(gradeSuffix);
    if (explicitGrade) {
      return { species, grade: explicitGrade, gradeFromSuffix: true, moistureContent };
    }
    return {
      species,
      grade: defaultGradeForSpecies(species, gradeFallback),
      gradeFromSuffix: false,
      moistureContent,
    };
  }

  return {
    species,
    grade: defaultGradeForSpecies(species, gradeFallback),
    gradeFromSuffix: false,
    moistureContent,
  };
}

/** Species label for RFQ matching — strips grade suffix and resolves aliases. */
export function speciesLabelForMatching(materialOrSpecies: string): string {
  const stripped = stripMaterialGradeSuffix(materialOrSpecies);
  return resolveCanonicalSpecies(stripped) ?? stripped;
}

/** @deprecated Use {@link speciesLabelForMatching} */
export function materialLabelForSpeciesMatch(material: string): string {
  return speciesLabelForMatching(material);
}
